import * as React from 'react';
import { Route, Switch, RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
import { ApplicationState } from '../../store';
import { ToxnotComponentProperties } from '../core/BaseApplicationComponents/ToxnotComponent';
import Layout from '../Layout';
import { LoadingSpinner, LoadingSpinnerSize } from '../core/LoadingSpinner';
import * as StartStateController from '../../store/StateControllers/Start/StartStateController';
import { ToxnotReactComponentPaths } from '../core/ToxnotReactComponentPaths';
import StartOrientation from './StartOrientation';
import StartReport from './StartReport';
import StartPassport from './StartPassport';
import InviteSuppliers from './InviteSuppliers';
import StartImport from './StartImport';

import './styles.css';
import { Link } from 'react-router-dom';
import StartSurveyGuide from './StartSurveyGuide';
import { ToxnotSignInRequiredComponent } from '../core/BaseApplicationComponents/ToxnotSignInRequiredComponent';
import { TeamRole } from '../../store/Models/UserContext';

// ===============================================================================

interface StartItem {
    localizedTitle: string;
    localizedDescription: string;
    additionalDescriptionHtml: JSX.Element | null;
    trackEventName: string;
    routeToPush: string | null;
    onStart: (() => void) | null;
}

// ===============================================================================

//
// Main component for the full view
//

type StartHomeProps =
    RouteComponentProps &
    ToxnotComponentProperties &
    StartStateController.StartState &
    typeof StartStateController.actionCreators; // ... plus action creators we've requested

class StartHome extends ToxnotSignInRequiredComponent<StartHomeProps> {
    private static readonly freTourId = 332956;
    private static readonly hasSeenFreLocalStorageKey = "has-seen-fre";

    public async componentDidMount() {
        this.props.appServices.trackEvent("get-started-viewed-home");

        //
        // Launch the FRE tour if the fre query param is present & the user hasn't already seen the FRE.
        // Remember whether or not we are launching the FRE so we don't show it again
        //

        const shouldShowFre = new URLSearchParams(document.location.search).get("fre") !== null;

        const hasSeenFre = localStorage.getItem(StartHome.hasSeenFreLocalStorageKey) ?? "false";

        if (shouldShowFre && (hasSeenFre !== "true")) {
            localStorage.setItem(StartHome.hasSeenFreLocalStorageKey, "true");
            Intercom("startTour", StartHome.freTourId);
        }

        //
        // fetch data we need from the service
        //

        await this.props.getStartData();
    }

    // ===============================================================================

    protected override getDisallowedRoles() {
        return [];
    }

    // ===============================================================================

    public renderConcreteComponent() {
        return (
            <Switch>
                <Route exact={true} path={"/start/invitesuppliers"}>
                    <Layout options={{ pinAppContentToWindowHeight: true, appBodyContentShouldTakeFullWidth: true }}>
                        <InviteSuppliers />
                    </Layout>
                </Route>
                <Route>
                    <Layout>
                        <div id="start-root" className="tn-section">
                            <div className="tn-section-row tn-section-heading-row">
                                <div className="tn-section-col-12">
                                    <h1>{this.localizeString("Get Started")}</h1>
                                </div>
                            </div>
                            <div className="tn-section-row">
                                <div className="tn-section-col-12">
                                    <Switch>
                                        <Route exact path={"/start"}>
                                            {this.renderStartContentRoot()}
                                        </Route>
                                        <Route>
                                            <div className="start-nav-container">
                                                <div className="sidebar">
                                                    <button type="button" className="btn btn-back" onClick={this.backButton_click}><span className="glyphicon glyphicon-chevron-left"></span></button>
                                                </div>
                                                <div className="content-container">
                                                    <Switch>
                                                        <Route path="/Start/Orientation">
                                                            <StartOrientation />
                                                        </Route>
                                                        <Route path="/Start/Report">
                                                            <StartReport />
                                                        </Route>
                                                        <Route path="/Start/Passport">
                                                            <StartPassport />
                                                        </Route>
                                                        <Route path="/Start/Import">
                                                            <StartImport />
                                                        </Route>
                                                        <Route path="/Start/SurveyGuide">
                                                            <StartSurveyGuide />
                                                        </Route>
                                                    </Switch>
                                                </div>
                                            </div>
                                        </Route>
                                    </Switch>
                                </div>
                            </div>
                        </div>
                    </Layout>
                </Route>
            </Switch>
        );
    }

    // ===============================================================================

    private renderStartItem(startItem: StartItem) {
        return (
            <div className="start-item-container">
                <div className="header">
                    <div className="title">
                        {startItem.localizedTitle}
                    </div>
                    <div className="description">
                        {startItem.localizedDescription}
                        {startItem.additionalDescriptionHtml}
                    </div>
                </div>
                <div className="action">
                    <button type="button" className="btn btn-tn-action-complement" onClick={e => this.startItem_click(startItem)}>{this.localizeString("Start")} <span className="glyphicon glyphicon-chevron-right"></span></button>
                </div>
            </div>
        );
    }

    // ===============================================================================

    private renderPassportStartItem() {
        return (
            this.renderStartItem({
                localizedTitle: this.localizeString("Share Passports on the Passport Library"),
                localizedDescription: this.localizeString("Securely share & manage your material data with others."),
                additionalDescriptionHtml: null,
                trackEventName: "get-started-share-passport-clicked",
                routeToPush: "/Start/Passport",
                onStart: null
            })
        );
    }

    // ===============================================================================

    private isMarketing() {
        return this.props.appServices.userContext?.role === TeamRole.Marketing;
    }

    // ===============================================================================

    private renderTaskList() {
        return (
            <div id="my-tasks-container">
                <h2>{this.localizeString("My Tasks")}</h2>
                {
                    (this.props.tasks.length > 0) &&
                    <div>
                        {this.props.tasks.orderByDescending(t => t.deadlineDate ?? new Date(0).toISOString()).take(5).map(task => <div key={task.taskId} className="my-tasks-item"><Link to={"Tasks/" + task.taskId}>{task.title.emptyCoalesce(this.localizeString("Unnamed Task"))}</Link><br /></div>)}
                        {this.props.tasks.length > 5 &&
                            <div className="my-tasks-item"><Link to={"Tasks/"}>{this.localizeStringFormat("and {0} more.", (this.props.tasks.length - 5))}</Link></div>
                        }
                    </div>
                }
                {
                    (!this.isMarketing()) && 
                    <p>
                        <a role="button" className="btn btn-tn-action-complement" onClick={e => this.fillOutSurveysButton_click()}>{this.localizeString("Fill out Surveys")} <span className="badge">{this.props.numSurveysToAnswer}</span></a>
                    </p>
                }
            </div>
        );
    }

    private renderHelpCenter() {
        return (
            <div id="help-center-container">
                <h2>{this.localizeString("Orientation")}</h2>
                <p>
                    <a href="http://help.exchange.3eco.com" target="_blank" onClick={e => this.props.appServices.trackEvent("get-started-help-articles-clicked")}>{this.localizeString("Help Article Library")} <span className="glyphicon glyphicon-arrow-right"></span></a>
                </p>
            </div>
        )
    }

    // ===============================================================================

    private renderStartContentRoot() {
        if (this.isMarketing()) {
            return (
                <div id="start-content-root" className="start-content right-content-shrink">
                    <div className="left-content">
                        {this.renderPassportStartItem()}
                    </div>
                    <div className="right-content">
                        <div className="separator">
                        </div>
                        <div className="help-content">
                            {this.props.isLoading
                                ? <LoadingSpinner size={LoadingSpinnerSize.Small} />
                                : (
                                    <React.Fragment>
                                        {this.renderTaskList()}
                                        {this.renderHelpCenter()}
                                    </React.Fragment>
                                )
                            }
                        </div>
                    </div>
                </div>
            );
        }
        return (
            <div id="start-content-root" className="start-content right-content-shrink">
                <div className="left-content">
                    {this.renderStartItem({
                        localizedTitle: this.localizeString("Create a Compliance or Sustainability Report"),
                        localizedDescription: this.localizeString("Use 3E Exchange to make a Compliance Report, Declare Label, HPD, or other Sustainability report."),
                        additionalDescriptionHtml: null,
                        trackEventName: "get-started-create-report-clicked",
                        routeToPush: "/Start/Report",
                        onStart: null
                    })}
                    {this.renderStartItem({
                        localizedTitle: this.localizeString("Get Supply Chain Data Fast"),
                        localizedDescription: this.localizeString("Search the Passport Library for existing Product Passports."),
                        additionalDescriptionHtml: null,
                        trackEventName: "get-started-get-supply-chain-data-clicked",
                        routeToPush: null,
                        onStart: this.startGetSupplyChainData_start
                    })}
                    {this.renderStartItem({
                        localizedTitle: this.localizeString("Request Data From Your Suppliers"),
                        localizedDescription: this.localizeString("Easily ask your suppliers for Product Passports with composition & compliance data."),
                        additionalDescriptionHtml:
                            <div>
                                <br />
                                <a role="button" onClick={e => this.startSurveyGuide_click()}>
                                    {this.localizeString("View our complete guide to collecting supply chain data")} <span className="glyphicon glyphicon-arrow-right"></span>
                                </a>
                            </div>,
                        trackEventName: "get-started-get-supply-chain-data-from-suppliers-clicked",
                        routeToPush: null,
                        onStart: this.startGetSupplyChainDataFromSuppliers_start
                    })}
                    {this.renderPassportStartItem()}
                    {this.renderStartItem({
                        localizedTitle: this.localizeString("Import Products, Materials, or Suppliers"),
                        localizedDescription: this.localizeString("We'll help you import your product or material libraries, update existing data, and import Suppliers."),
                        additionalDescriptionHtml: null,
                        trackEventName: "get-started-import-data-clicked",
                        routeToPush: "/Start/Import",
                        onStart: null
                    })}
                    {/*    {this.renderStartItem({*/}
                    {/*        iconSrc: "",*/}
                    {/*        localizedTitle: this.localizeString("Import Data, Screen Hazards & Assess Compliance"),*/}
                    {/*        localizedDescription: this.localizeString("We'll help you import your product & material data so you can automatically screen for hazards and asess compliance with regulations"),*/}
                    {/*        trackEventName: "get-started-import-screen-and-assess-clicked",*/}
                    {/*        routeToPush: null,*/}
                    {/*        onStart: null*/}
                    {/*    })}*/}
                </div>
                <div className="right-content">
                    <div className="separator">
                    </div>
                    <div className="help-content">
                        {this.props.isLoading
                            ? <LoadingSpinner size={LoadingSpinnerSize.Small} />
                            : (
                                <React.Fragment>
                                    {this.renderTaskList()}
                                    {this.renderHelpCenter()}
                                </React.Fragment>
                            )
                        }
                    </div>
                </div>
            </div>
        );
    }

    // ===============================================================================

    //
    // UI Events
    //

    // ===============================================================================

    private takeATourLink_click = () => {
        this.props.appServices.trackEvent("get-started-take-a-tour-clicked");
        Intercom("startTour", StartHome.freTourId);
    }

    // ===============================================================================

    private backButton_click = () => {
        this.props.history.goBack();
    }

    // ===============================================================================

    private startItem_click(startItem: StartItem) {
        this.props.appServices.trackEvent(startItem.trackEventName);

        if (startItem.routeToPush !== null) {
            this.props.history.push(startItem.routeToPush);
        }

        if (startItem.onStart !== null) {
            startItem.onStart();
        }
    }

    // ===============================================================================

    private startSurveyGuide_click() {
        this.props.appServices.trackEvent("get-started-learn-more-surveys-clicked");
        this.props.history.push("/Start/SurveyGuide");
    }

    // ===============================================================================

    private startGetSupplyChainData_start = () => {
        this.props.history.push(ToxnotReactComponentPaths.SharedMaterialsHomeStartPath);
    }

    // ===============================================================================

    private startGetSupplyChainDataFromSuppliers_start = () => {
        this.props.history.push("/Start/InviteSuppliers");
    }

    // ===============================================================================

    private fillOutSurveysButton_click = () => {
        this.props.appServices.trackEvent("get-started-fill-out-surveys-clicked");

        window.location.href = "/Suppliers/SupplierDashboard";
    }
}

// ===============================================================================

export default connect(
    (state: ApplicationState) => ({ ...state.startState, appServices: state.appServices }),
    {
        ...StartStateController.actionCreators // Selects which action creators are merged into the component's props
    }
)(StartHome as any);
